import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Mail, Lock, AlertCircle } from 'lucide-react';

export const Login = () => {
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // When the global 401 interceptor bounces the user here with `?expired=1`,
  // surface a clear "your session ended" message so they know they haven't
  // done anything wrong.
  useEffect(() => {
    if (searchParams.get('expired') === '1') {
      setError(language === 'pt'
        ? 'A sua sessão expirou. Por favor, entre novamente.'
        : 'Your session expired. Please sign in again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/cv/new');
    } catch (err) {
      // Always show the most specific error available so production failures
      // are diagnosable (HTTP status + backend detail) instead of just "error".
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message;
      let msg;
      if (status === 401) {
        msg = t('language') === 'en' ? 'Wrong email or password' : 'Email ou palavra-passe incorretos';
      } else if (status === 423 || status === 429) {
        msg = detail || t('error');
      } else if (status >= 500) {
        msg = `Erro do servidor (HTTP ${status}). Tente novamente em alguns segundos.`;
      } else if (!status) {
        msg = 'Sem ligação ao servidor. Verifique a sua internet.';
      } else {
        msg = detail || t('error');
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-6">
            <img src="/evolutt_logo_dark.png" alt="Evolutt" className="h-12 w-auto" />
          </Link>
        </div>

        <Card className="shadow-lg border-0" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {t('login')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700" data-testid="login-error">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#0f172a]">
                  {t('email')}
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="pl-10 input-focus"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-[#0f172a]">
                  {t('passwordPlaceholder')}
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 input-focus"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary py-5"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="loading-spinner w-5 h-5"></div>
                ) : (
                  t('login')
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-[#1E3C3C] font-medium hover:underline" data-testid="forgot-password-link">
                Esqueceu a password?
              </Link>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-[#64748b]">
                {t('noAccount')}{' '}
                <Link to="/register" className="text-[#1E3C3C] font-medium hover:underline" data-testid="register-link">
                  {t('register')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
